import { PayPalButtons } from '@paypal/react-paypal-js';
import { LoadingSpinner } from '@paypalcorp/pp-react';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Spacer from '../../../components/Spacer';
import { CAPTURE_INTENT, SDK_FLOW_SLUG } from '../../../constants/sdk';
import { trackButtonClick } from '../../../lib/fpti';
import { isUserLoggedInSelector } from '../../../store/App/selectors';
import {
  authorizeOrder,
  captureAuthorize,
  captureOrder,
  createOrder,
  shippingAddressChange,
  shippingOptionsChange
} from '../../../util/sdkCallbacks';
import styles from '../../IntegrationPageSection/PreviewWrapper/Preview.module.less';
import TestCardsWrapper from '../../IntegrationPageSection/PreviewWrapper/TestCardsWrapper';
import IntegrationPageSectionContext from '../../IntegrationPageSection/StateManagement/IntegrationPageSectionContext';
import TestPanelContext from '../../IntegrationPageSection/StateManagement/TestPanelContext';
import CustomizedCardFieldsAndBillingAddress from './CustomizedCardFieldsAndBillingAddress';
import { SellerCardsWrapper } from '../../IntegrationPageSection/PreviewWrapper/SellerCardsWrapper';
import { PARTNER } from '../../../lib/constants';

const AdvancedPreviewWrapper = () => {
  const [buttonLoading, setButtonLoading] = useState(true);
  const isUserLoggedIn = useSelector(isUserLoggedInSelector);
  const {
    ButtonConfigurator: { buttonSettings } = {},
    SdkConfigurator: { sdkSettings },
    OrderConfigurator: { amount: { value: orderAmount } = {}, sellerId, updateSellerId } = {},
    apps,
    code: { status: { handleShippingAddress = false, configureButtonMessages = false } = {} } = {},
    lastCapture: { updateLastCapture }
  } = useContext(IntegrationPageSectionContext);
  const { updateLogs, toggleTestPanel } = useContext(TestPanelContext);
  const { clientId } = sdkSettings;
  const csrfToken = useSelector((state) => state?.app?.csrfToken);
  const appIntegrationType = useSelector((state) => state?.app?.appIntegrationType);
  useEffect(() => {
    setButtonLoading(true);
  }, [
    sdkSettings,
    buttonSettings,
    orderAmount,
    handleShippingAddress,
    configureButtonMessages,
    sellerId
  ]);

  const handleCreateOrder = async ({ paymentSource, threeDsSetting }) => {
    try {
      const orderId = await createOrder({
        currencyCode: sdkSettings?.currency,
        amount: orderAmount,
        intent: sdkSettings?.intent,
        handleShippingAddress,
        csrfToken,
        updateLogs,
        clientId: isUserLoggedIn ? clientId : null,
        product: SDK_FLOW_SLUG.ADVANCED,
        paymentSource,
        threeDsSetting,
        appIntegrationType,
        sellerId
      });
      toggleTestPanel(true);
      return orderId;
    } catch {
      return null;
    }
  };

  const handleCaptureOrder = async ({ orderID }) => {
    await captureOrder({
      orderId: orderID,
      csrfToken,
      updateLogs,
      updateLastCapture,
      clientId: isUserLoggedIn ? clientId : null,
      appIntegrationType,
      sellerId
    });
    toggleTestPanel(true);
  };

  const handleAuthorizeOrder = async ({ orderID }) => {
    const authorizationId = await authorizeOrder({
      orderId: orderID,
      clientId: isUserLoggedIn ? clientId : null,
      csrfToken,
      updateLogs,
      appIntegrationType,
      sellerId
    });
    if (authorizationId) {
      await captureAuthorize({
        authorizationId,
        csrfToken,
        updateLogs,
        updateLastCapture,
        clientId: isUserLoggedIn ? clientId : null,
        appIntegrationType,
        sellerId
      });
    }
    toggleTestPanel(true);
  };

  const handleOrderIntent = (data) => {
    if (sdkSettings.intent === CAPTURE_INTENT) {
      handleCaptureOrder(data);
    } else {
      handleAuthorizeOrder(data);
    }
  };

  const handleShippingAddressChange = (data) => {
    if (handleShippingAddress) {
      shippingAddressChange(data, updateLogs);
    }
  };

  const handleShippingOptionsChange = (data) => {
    if (handleShippingAddress) {
      shippingOptionsChange(data, updateLogs);
    }
  };

  return (
    <div className={styles.preview_wrapper} id="guide-preview-wrapper">
      {appIntegrationType === PARTNER && isUserLoggedIn && apps.length > 0 && (
        <div className={styles.seller_cards_container}>
          <SellerCardsWrapper updateSellerId={updateSellerId} />
        </div>
      )}

      <div className={styles.acdc_test_cards_container}>
        <TestCardsWrapper showRadioButtons />
      </div>
      <div className={styles.preview_checkout_wrapper}>
        <div className={styles.preview_checkout_container}>
          {buttonLoading && (
            <div className={styles.preview_loader_container}>
              <LoadingSpinner
                className={styles.preview_loader}
                screenReaderText="loading"
                size="md"
              />
            </div>
          )}
          <PayPalButtons
            style={buttonSettings}
            fundingSource=""
            forceReRender={[buttonSettings, orderAmount, handleShippingAddress, sellerId]}
            onInit={() => setButtonLoading(false)}
            createOrder={handleCreateOrder}
            onApprove={handleOrderIntent}
            onClick={(data) => trackButtonClick(data.fundingSource)}
            {...(handleShippingAddress
              ? {
                  onShippingAddressChange: (data) => handleShippingAddressChange(data),
                  onShippingOptionsChange: (data) => handleShippingOptionsChange(data)
                }
              : {})}
            {...(configureButtonMessages ? { message: { amount: Number(orderAmount) } } : {})}
          />
          <Spacer />
          <CustomizedCardFieldsAndBillingAddress
            handleCreateOrder={handleCreateOrder}
            handleOrderIntent={handleOrderIntent}
          />
        </div>
      </div>
    </div>
  );
};

AdvancedPreviewWrapper.propTypes = {
  isUserLoggedIn: PropTypes.bool.isRequired
};

export default AdvancedPreviewWrapper;
